import React, { useContext } from 'react';
import { Skeleton, Tag, theme, Tooltip } from 'antd';
import { CalculatorOutlined, CheckCircleFilled, ClockCircleFilled, ClockCircleOutlined,
  CloseCircleFilled, CreditCardOutlined, PaperClipOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import ThemeContext from '../context/ThemeContext';
import { formatExpenseAmount, getExpensePaymentState, getExpenseRemaining } from './expenseWorkspaceUtils';
import './expense-workspace.css';

export function useExpenseWorkspaceStyle() {
  const { token } = theme.useToken();
  const appearance = useContext(ThemeContext);
  return {
    '--expense-bg': token.colorBgContainer, '--expense-soft': token.colorFillAlter,
    '--expense-line': token.colorBorderSecondary, '--expense-text': token.colorText,
    '--expense-heading': appearance?.isDarkMode ? token.colorTextHeading : token.colorPrimaryTextActive,
    '--expense-muted': token.colorTextSecondary,
    '--expense-color-scheme': appearance?.isDarkMode ? 'dark' : 'light',
    '--expense-primary-bg': token.colorPrimary, '--expense-primary-hover': token.colorPrimaryHover,
    '--expense-primary-active': token.colorPrimaryActive, '--expense-primary-text': token.colorTextLightSolid,
    '--expense-accent': appearance?.isDarkMode ? token.blue7 : token.colorPrimary, '--expense-success': token.colorSuccess,
    '--expense-warning': token.colorWarning, '--expense-danger': token.colorError,
    '--expense-warning-text': token.colorTextSecondary, '--expense-danger-text': token.colorErrorTextActive,
    '--expense-info-bg': token.blue1, '--expense-success-bg': token.colorSuccessBg,
    '--expense-warning-bg': token.colorWarningBg, '--expense-danger-bg': token.colorErrorBg,
  };
}

export function ExpensePaymentTag({ expense }) {
  const { token } = theme.useToken();
  const state = getExpensePaymentState(expense);
  const style = state === 'Payé' ? { color: 'success', icon: <CheckCircleFilled aria-hidden="true" style={{ color: token.colorSuccessTextActive }} /> } :
    state === 'Partiellement payé' ? { color: 'warning', icon: <ClockCircleFilled aria-hidden="true" style={{ color: token.colorWarningTextActive }} /> } :
      { color: 'error', icon: <CloseCircleFilled aria-hidden="true" style={{ color: token.colorErrorTextActive }} /> };
  return <Tag {...style} style={{ color: token.colorText }}>{state}</Tag>;
}

export function ExpenseDueDate({ expense, asOfDate }) {
  const due = dayjs(expense.due_date);
  if (!expense.due_date || !due.isValid()) return <span className="expense-workspace__muted">Non renseignée</span>;
  const today = dayjs(asOfDate || dayjs().format('YYYY-MM-DD'));
  const paid = getExpensePaymentState(expense) === 'Payé' || getExpenseRemaining(expense) <= 0;
  const overdue = !paid && due.isBefore(today, 'day');
  const now = !paid && due.isSame(today, 'day');
  return <div className="expense-workspace__due">
    <time dateTime={due.format('YYYY-MM-DD')}>{due.format('DD/MM/YYYY')}</time>
    <small className={overdue ? 'expense-workspace__overdue' : now ? 'expense-workspace__today' : ''}>
      {paid ? 'Réglée' : overdue ? <><WarningOutlined aria-hidden="true" /> En retard</> : now ? <><ClockCircleOutlined aria-hidden="true" style={{ color: 'var(--expense-warning)' }} /> Aujourd’hui</> : 'À venir'}
    </small>
  </div>;
}

export function ExpenseIdentity({ expense, onDocument }) {
  return <div className="expense-workspace__identity">
    <Tooltip title={expense.category_name || 'Charge sans catégorie'}>
      <strong>{expense.category_name || 'Charge sans catégorie'}</strong>
    </Tooltip>
    <div className="expense-workspace__metadata">
      <span title={expense.supplier_name || 'Fournisseur non renseigné'}>{expense.supplier_name || 'Fournisseur non renseigné'}</span>
      <span className="expense-workspace__reference" title={expense.bill || 'Sans référence'}>{expense.bill || 'Sans référence'}</span>
      {expense.document_url && expense.document_name && <Tooltip title={`Voir ${expense.document_name}`}>
        <button type="button" onClick={() => onDocument(expense.id, expense.document_name)}
          aria-label={`Voir le justificatif de ${expense.bill || expense.category_name || expense.id}`}>
          <PaperClipOutlined />
        </button>
      </Tooltip>}
    </div>
  </div>;
}

export function ExpenseSummary({ summary, loading }) {
  const missingDueDates = Number(summary?.missing_due_date_count);
  const data = [
    { label: 'Total des charges', value: summary?.total_amount, icon: <CalculatorOutlined />, kind: 'info',
      note: 'Période et filtres sélectionnés · tous statuts' },
    { label: 'Montant payé', value: summary?.paid_amount, icon: <CreditCardOutlined />, kind: 'success' },
    { label: 'Reste à payer', value: summary?.remaining_amount, icon: <ClockCircleOutlined />, kind: 'warning',
      note: summary ? `${summary.outstanding_count} charge${summary.outstanding_count === 1 ? '' : 's'} avec un solde` : '' },
    { label: 'Dont échu', value: summary?.overdue_amount, icon: <WarningOutlined />, kind: 'danger',
      note: summary ? <>
        <span>{summary.overdue_count} charge{summary.overdue_count === 1 ? '' : 's'} échue{summary.overdue_count === 1 ? '' : 's'}</span>
        {missingDueDates > 0 && <span className="expense-workspace__missing-due"
          title="Ces charges restant à régler n’ont pas d’échéance et ne sont pas incluses dans le montant échu.">
          <WarningOutlined aria-hidden="true" /> {missingDueDates} charge{missingDueDates === 1 ? '' : 's'} sans échéance
        </span>}
        {!Number.isFinite(missingDueDates) && <span className="expense-workspace__missing-due">Échéances manquantes non évaluées</span>}
      </> : '' },
  ];
  return <dl className="expense-workspace__summary" aria-label="Situation des charges administratives" aria-busy={loading}>
    {data.map((item) => <div key={item.label} className="expense-workspace__metric">
      <span className={`expense-workspace__metric-icon expense-workspace__metric-icon--${item.kind}`} aria-hidden="true">{item.icon}</span>
      <div className="expense-workspace__metric-content">
        <dt>{item.label}</dt>
        <dd>{loading ? <Skeleton.Input active size="small" /> : formatExpenseAmount(item.value)}</dd>
        {item.note && <small>{item.note}</small>}
      </div>
    </div>)}
  </dl>;
}
